import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Link, RouterProvider } from 'react-router';
import { Layout } from './components/Layout';
import { ToastProvider } from './components/Toast';
import { AuthProvider } from './lib/auth';
import { useDocumentTitle } from './lib/useDocumentTitle';
import { HomePage } from './pages/Home';

const ArtistPage = lazy(() => import('./pages/Artist').then((m) => ({ default: m.ArtistPage })));
const SearchPage = lazy(() => import('./pages/Search').then((m) => ({ default: m.SearchPage })));
const MyArtistsPage = lazy(() => import('./pages/MyArtists').then((m) => ({ default: m.MyArtistsPage })));
const CallbackPage = lazy(() => import('./pages/Callback').then((m) => ({ default: m.CallbackPage })));
const PrivacyPage = lazy(() => import('./pages/Legal').then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/Legal').then((m) => ({ default: m.TermsPage })));
const TastePage = lazy(() => import('./pages/Taste').then((m) => ({ default: m.TastePage })));

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: true, gcTime: 30 * 60_000 } },
});

function Page({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="page" aria-busy="true" />}>{children}</Suspense>;
}

function NotFound() {
  useDocumentTitle('Not found');
  return (
    <div className="page" style={{ gap: 16 }}>
      <h1 className="disp page-title">Nothing here</h1>
      <p className="muted">That page doesn’t exist.</p>
      <Link to="/" className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
        Go home
      </Link>
    </div>
  );
}

function RouteError() {
  return (
    <div className="page" style={{ gap: 16 }} role="alert">
      <h1 className="disp page-title">Something broke</h1>
      <p className="muted">Reload the page to try again.</p>
      <button type="button" className="btn btn-primary" style={{ alignSelf: 'flex-start' }} onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'artist/:id', element: <Page><ArtistPage /></Page> },
      { path: 'search', element: <Page><SearchPage /></Page> },
      { path: 'my-artists', element: <Page><MyArtistsPage /></Page> },
      { path: 'taste', element: <Page><TastePage /></Page> },
      { path: 'callback', element: <Page><CallbackPage /></Page> },
      { path: 'privacy', element: <Page><PrivacyPage /></Page> },
      { path: 'terms', element: <Page><TermsPage /></Page> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
